import { activateSellerFirebaseAuthSession, signInSellerFirebaseAuth, signOutSellerFirebaseAuth } from "@/lib/firebase/auth-session";
import { useEffect, useState, useSyncExternalStore } from "react";
import { useQueryClient, type Query, type QueryKey } from "@tanstack/react-query";
import { createRevisionBus, createKeyedRevisionRefresh, type RevisionEvent, type RealtimeStatus } from "@a2z/realtime";
import { startFirebaseCredential, subscribeAuthenticatedRevisions } from "@a2z/realtime/firebase";
import { getFirebaseAuth, getFirebaseFirestore } from "@/lib/firebase/config";
import { getPrivateSessionState, subscribePrivateSession } from "@/lib/api/private-session";

export type SellerRealtimeEvent = RevisionEvent & { storeId: string; sellerId: string };
const events = createRevisionBus<SellerRealtimeEvent>();
export const subscribeSellerRealtime = events.subscribe;

export function sellerRevisionAffects(key: QueryKey, storeSlug: string, topics: readonly string[]) {
  if (key[0] !== "seller" || key[1] !== storeSlug) return false;
  const resource = String(key[2]);
  return (topics.includes("meta") && resource === "meta") ||
    (topics.includes("access") && ["session", "store-subscription", "store-wallet", "members", "roles", "invites"].includes(resource)) ||
    (topics.includes("orders") && ["orders", "dashboard-analytics", "transactions", "customers", "prepared-carts"].includes(resource)) ||
    (topics.includes("menu") && ["products", "categories", "store", "store-settings", "settings", "payment-options", "shipping-rules", "area-rates", "coupons", "reviews"].includes(resource)) ||
    (topics.includes("customDomain") && resource === "custom-domain");
}

/** Revision-only Firestore documents; the authenticated API still owns records. */
export function useSellerRealtime(sellerId?: string, storeId?: string, firebaseToken?: string | null, storeAccess = true) {
  const client = useQueryClient();
  const [status, setStatus] = useState<RealtimeStatus>("connecting");
  const lifetime = useSyncExternalStore(subscribePrivateSession, getPrivateSessionState);
  useEffect(() => {
    if (!sellerId || lifetime.changing) {
      void signOutSellerFirebaseAuth().catch(() => undefined);
      return;
    }
    // A failed optional mint must not discard the SDK's still-valid same-scope
    // identity. readSession signs out explicitly when authority changes.
    if (!firebaseToken) {
      const auth = getFirebaseAuth();
      if (!auth || auth.currentUser?.uid === sellerId) return;
      let active = true;
      // Report after the listener's initial connecting state, without retrying /me.
      queueMicrotask(() => { if (active) setStatus(current => current === "unconfigured" ? current : "degraded"); });
      return () => { active = false; };
    }
    const generation = lifetime.generation;
    const isCurrent = () => !getPrivateSessionState().changing && getPrivateSessionState().generation === generation;
    activateSellerFirebaseAuthSession();
    return startFirebaseCredential({
      signIn: () => signInSellerFirebaseAuth(firebaseToken),
      isAuthenticated: () => getFirebaseAuth()?.currentUser?.uid === sellerId,
      isCurrent, onError: () => setStatus("degraded"),
    });
  }, [firebaseToken, lifetime.changing, lifetime.generation, sellerId]);
  useEffect(() => () => { void signOutSellerFirebaseAuth().catch(() => undefined); }, []);
  useEffect(() => {
    const storeSlug = lifetime.storeSlug;
    if (!sellerId || !storeId || !storeSlug || lifetime.changing) return;
    const auth = getFirebaseAuth();
    const db = getFirebaseFirestore();
    if (!auth || !db) {
      let active = true;
      queueMicrotask(() => { if (active) setStatus("unconfigured"); });
      if (import.meta.env.DEV) console.warn("Live updates are not configured for this portal.");
      return () => { active = false; };
    }
    const generation = lifetime.generation;
    const isCurrent = () => !getPrivateSessionState().changing && getPrivateSessionState().generation === generation;
    const refresh = createKeyedRevisionRefresh<Query>({ isCurrent, refresh: async (query) => {
      // Await only this key's earlier snapshot, never an unrelated slow read.
      if (query.state.fetchStatus === "fetching") await query.promise?.catch(() => undefined);
      if (isCurrent() && query.isActive() && client.getQueryCache().get(query.queryHash) === query) {
        await client.refetchQueries({ queryKey: query.queryKey, exact: true, type: "active" }, { cancelRefetch: false });
      }
    } });
    const stop = subscribeAuthenticatedRevisions({
      auth, db, ownerId: sellerId, ownerType: "SELLER", storeId, isCurrent, onStatus: setStatus,
      sources: [
        ...(storeAccess ? [{ path: `realtimeStores/${storeId}`, topics: ["menu", "meta", "customDomain", "access", "orders", "notifications"] }] : []),
        { path: `realtimeOwners/${sellerId}`, topics: ["access", "notifications"] },
      ],
      onChange: (event) => {
        if (!isCurrent()) return;
        events.emit({ ...event, sellerId, storeId });
        const predicate = (query: { queryKey: QueryKey }) => sellerRevisionAffects(query.queryKey, storeSlug, event.topics);
        // Inactive entries become stale without issuing requests.
        void client.invalidateQueries({ predicate, refetchType: "none" });
        client.getQueryCache().findAll({ predicate }).forEach((query) => {
          if (query.isActive()) refresh.enqueue(query, event);
        });
      },
    });
    return () => { refresh.dispose(); stop(); };
  }, [client, lifetime.changing, lifetime.generation, lifetime.storeSlug, sellerId, storeAccess, storeId]);
  return status;
}
